import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import WebsiteNavbar from "../components/WebsiteNavbar";
import Footer from "../components/Footer";
import InvoiceModal from "../components/InvoiceModal";
import { getProfile, getBookings, getMyInvoices, getInvoice, logout, updateProfile } from "../services/authService";
import { formatPerthDate, getPerthGreeting } from "../utils/perthTime";
import api from "../services/api";
import {
  FaUserCircle, FaClipboardList, FaLock, FaSignOutAlt, FaTruck,
  FaQuoteRight, FaChevronDown, FaHeadset,
  FaEye, FaEyeSlash, FaCheck, FaTimes, FaFilter, FaFileInvoiceDollar,
  FaGift, FaPlus, FaSearch, FaCalendarAlt, FaMapMarkerAlt,
  FaDownload, FaCheckCircle,
} from "react-icons/fa";
import "../style/myAccount.css";

/* ─── constants ─── */
const NAV_ITEMS = [
  { key: "overview", icon: <FaTruck />, label: "Dashboard Overview" },
  { key: "bookings", icon: <FaClipboardList />, label: "My Bookings" },
  { key: "invoices", icon: <FaFileInvoiceDollar />, label: "Tax Invoices" },
  { key: "profile", icon: <FaUserCircle />, label: "Profile Information" },
  { key: "password", icon: <FaLock />, label: "Change Password" },
  { key: "logout", icon: <FaSignOutAlt />, label: "Log Out" },
];

const STATUS_CLASS = {
  completed: "status-completed",
  confirmed: "status-confirmed",
  active: "status-active",
  new: "status-new",
  cancelled: "status-cancelled",
  pending: "status-pending",
  collection_due: "status-active",
};

const fmt = (d) => formatPerthDate(d);

/* ════════════════════════════════════════════
   SECTION 1: Dashboard Overview (Home)
════════════════════════════════════════════ */
const parseDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  return isNaN(date.getTime()) ? null : date;
};

const DashboardOverview = ({ customer, bookings, invoices, navigate, setTab, onOpenInvoice }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Active On-Site: delivery date has arrived/passed and collection date is not past, not cancelled or completed
  const activeOnSiteBookings = bookings.filter((b) => {
    const status = b.status?.toLowerCase();
    if (status === "cancelled" || status === "completed") return false;
    const del = parseDate(b.delivery_date);
    if (!del) return false;
    del.setHours(0, 0, 0, 0);
    if (del > today) return false; // Future delivery is NOT active on site yet!

    const col = parseDate(b.collection_date);
    if (col) {
      col.setHours(23, 59, 59, 999);
      return today <= col;
    }
    return true;
  });

  // Upcoming: delivery date is in the future, not cancelled or completed
  const upcomingBookings = bookings
    .filter((b) => {
      const status = b.status?.toLowerCase();
      if (status === "cancelled" || status === "completed") return false;
      const del = parseDate(b.delivery_date);
      if (!del) return false;
      del.setHours(0, 0, 0, 0);
      return del > today;
    })
    .sort((a, b) => new Date(a.delivery_date) - new Date(b.delivery_date));

  // Determine what to spotlight: active on-site bin takes precedence, otherwise next upcoming delivery
  let spotlightBooking = null;
  let spotlightType = "active";
  let spotlightTitle = "Current Active Skip Bin";

  if (activeOnSiteBookings.length > 0) {
    spotlightBooking = activeOnSiteBookings[0];
    spotlightType = "active";
    spotlightTitle = "Current Active Skip Bin";
  } else if (upcomingBookings.length > 0) {
    spotlightBooking = upcomingBookings[0];
    spotlightType = "upcoming";
    spotlightTitle = "Upcoming Scheduled Hire";
  }

  // Loyalty calculations: "Hire 6 Bins, Get the 7th FREE" (6 required paid hires -> 7th is Free)
  const totalBookingsCount = bookings.length;
  const cycleCount = totalBookingsCount % 7;
  const loyaltyProgress = Math.min(6, cycleCount); // 0 to 6 completed
  const remainingForFreeBin = 6 - loyaltyProgress; // 0 to 6 hires away

  const getGreeting = () => getPerthGreeting();

  return (
    <div className="ma-main-body">
      {/* HERO WELCOME BANNER */}
      <div className="ma-hero-welcome">
        <div className="ma-hero-text">
          <h1 className="ma-title">
            {getGreeting()}, {customer?.first_name || "Valued Customer"}! 👋
          </h1>
          <p className="ma-subtitle">
            Book, manage, and track your skip bin hire with ease.
          </p>
        </div>
        <div className="ma-hero-actions">
          <button className="ma-hero-book-btn" onClick={() => navigate("/booking")}>
            <FaPlus /> Book a Skip Bin
          </button>
        </div>
      </div>

      {/* METRIC CARDS */}
      <div className="ma-metrics-grid">
        <div className="ma-metric-card ma-metric-blue" onClick={() => setTab("bookings")}>
          <div className="ma-metric-icon">
            <FaTruck />
          </div>
          <div className="ma-metric-content">
            <span className="ma-metric-num">
              {activeOnSiteBookings.length > 0
                ? activeOnSiteBookings.length
                : upcomingBookings.length > 0
                  ? upcomingBookings.length
                  : 0}
            </span>
            <span className="ma-metric-lbl">
              {activeOnSiteBookings.length > 0
                ? "Active On-Site"
                : upcomingBookings.length > 0
                  ? "Upcoming Deliveries"
                  : "Active Deliveries"}
            </span>
          </div>
        </div>

        <div className="ma-metric-card ma-metric-green" onClick={() => setTab("bookings")}>
          <div className="ma-metric-icon">
            <FaClipboardList />
          </div>
          <div className="ma-metric-content">
            <span className="ma-metric-num">{bookings.length}</span>
            <span className="ma-metric-lbl">Total Bins Hired</span>
          </div>
        </div>

        <div className="ma-metric-card ma-metric-orange" onClick={() => setTab("invoices")}>
          <div className="ma-metric-icon">
            <FaFileInvoiceDollar />
          </div>
          <div className="ma-metric-content">
            <span className="ma-metric-num">{invoices.length > 0 ? invoices.length : bookings.length}</span>
            <span className="ma-metric-lbl">Saved Invoices</span>
          </div>
        </div>

        <div className="ma-metric-card ma-metric-purple loyalty-metric" onClick={() => setTab("overview")}>
          <div className="ma-metric-icon">
            <FaGift />
          </div>
          <div className="ma-metric-content">
            <span className="ma-metric-num">
              {remainingForFreeBin === 0 ? "Free Bin Ready!" : `${remainingForFreeBin} ${remainingForFreeBin === 1 ? "Hire" : "Hires"} Away`}
            </span>
            <span className="ma-metric-lbl">Free Bin Reward</span>
          </div>
        </div>
      </div>

      {/* LOYALTY PROGRESS BAR CARD */}
      <div className="ma-loyalty-banner">
        <div className="ma-loyalty-info">
          <div className="ma-loyalty-title-wrap">
            <FaGift className="ma-loyalty-icon" />
            <div>
              <h3>Jerry's Loyalty Club: Hire 6 Bins, Get the 7th FREE!</h3>
              <p>
                You have completed <strong>{totalBookingsCount}</strong> bin hire{totalBookingsCount !== 1 ? "s" : ""}.
                {remainingForFreeBin === 0
                  ? " 🎉 Congratulations! You have completed 6 hires — your next (7th) bin is 100% FREE!"
                  : ` Book ${remainingForFreeBin} more bin${remainingForFreeBin > 1 ? "s" : ""} to unlock your free bin.`}
              </p>
            </div>
          </div>
          <div className="ma-loyalty-bar-wrap">
            <div className="ma-loyalty-bar-bg">
              <div
                className="ma-loyalty-bar-fill"
                style={{ width: `${Math.min(100, (loyaltyProgress / 6) * 100)}%` }}
              />
            </div>
            <span className="ma-loyalty-bar-text">
              {loyaltyProgress === 6 ? "6 / 6 Completed (Reward Ready!)" : `${loyaltyProgress} / 6 Completed`}
            </span>
          </div>
        </div>
      </div>

      {/* ACTIVE / UPCOMING HIRE SPOTLIGHT */}
      {spotlightBooking && (
        <div className={`ma-active-tracker-card ${spotlightType === "upcoming" ? "ma-upcoming-card" : ""}`}>
          <div className="ma-active-header">
            <div className="ma-active-title-group">
              <h3 className="ma-active-heading">{spotlightTitle}</h3>
              {spotlightBooking.booking_ref && (
                <span className="ma-ref-tag">{spotlightBooking.booking_ref}</span>
              )}
            </div>
            <button className="ma-view-inv-btn" onClick={() => onOpenInvoice(spotlightBooking)}>
              <FaFileInvoiceDollar /> View Tax Invoice
            </button>
          </div>

          <div className="ma-active-details-grid">
            <div className="ma-active-col">
              <div className="ma-active-col-head">
                <span className="ma-active-col-icon icon-blue"><FaTruck /></span>
                <span className="ma-col-lbl">Bin Size & Waste</span>
              </div>
              <strong className="ma-col-val">{spotlightBooking.bin_size || spotlightBooking.size || "Standard"} Skip Bin</strong>
              <span className="ma-waste-pill">{spotlightBooking.waste_name || "Green Waste"}</span>
            </div>

            <div className="ma-active-col">
              <div className="ma-active-col-head">
                <span className="ma-active-col-icon icon-green"><FaCalendarAlt /></span>
                <span className="ma-col-lbl">Delivery Date</span>
              </div>
              <strong className="ma-col-val">{fmt(spotlightBooking.delivery_date)}</strong>
            </div>

            <div className="ma-active-col">
              <div className="ma-active-col-head">
                <span className="ma-active-col-icon icon-orange"><FaCalendarAlt /></span>
                <span className="ma-col-lbl">Scheduled Collection</span>
              </div>
              <strong className="ma-col-val">{fmt(spotlightBooking.collection_date)}</strong>
            </div>

            <div className="ma-active-col">
              <div className="ma-active-col-head">
                <span className="ma-active-col-icon icon-purple"><FaMapMarkerAlt /></span>
                <span className="ma-col-lbl">Delivery Site</span>
              </div>
              <strong className="ma-col-val ma-address-truncate" title={spotlightBooking.delivery_address || "Standard Metro Drop"}>
                {spotlightBooking.delivery_address || "Standard Metro Drop"}
              </strong>
            </div>
          </div>
        </div>
      )}

      {/* QUICK ACTIONS ROW */}
      <div className="ma-quick-actions-row">
        <div className="ma-action-box" onClick={() => navigate("/booking")}>
          <div className="ma-action-icon" style={{ background: "#FFF7ED", color: "#F5821F" }}>
            <FaPlus />
          </div>
          <h4>Book a Skip Bin</h4>
          <p>Instant online booking with fast metro delivery.</p>
        </div>

        <div className="ma-action-box" onClick={() => setTab("bookings")}>
          <div className="ma-action-icon" style={{ background: "#EFF6FF", color: "#3B82F6" }}>
            <FaClipboardList />
          </div>
          <h4>Manage Bookings</h4>
          <p>Check schedules, dates, and live hire progress.</p>
        </div>

        <div className="ma-action-box" onClick={() => setTab("invoices")}>
          <div className="ma-action-icon" style={{ background: "#F0FDF4", color: "#10B981" }}>
            <FaFileInvoiceDollar />
          </div>
          <h4>Tax Invoices</h4>
          <p>Instant PDF download and payment records.</p>
        </div>

        <div className="ma-action-box" onClick={() => navigate("/commercial-quotes")}>
          <div className="ma-action-icon" style={{ background: "#FAF5FF", color: "#A855F7" }}>
            <FaQuoteRight />
          </div>
          <h4>Custom Quote</h4>
          <p>Special waste types, commercial or large projects.</p>
        </div>
      </div>

      {/* RECENT BOOKINGS & INVOICES */}
      <div className="ma-card">
        <div className="ma-card-header">
          <div className="ma-card-title-group">
            <FaClipboardList style={{ color: "#F5821F" }} />
            <h2>Recent Bookings & Receipts</h2>
          </div>
          <button className="ma-view-all-btn" onClick={() => setTab("bookings")}>
            View All ({bookings.length}) →
          </button>
        </div>

        {/* DESKTOP / TABLET TABLE VIEW */}
        <div className="ma-table-wrap ma-desktop-table">
          <table className="ma-table">
            <thead>
              <tr>
                <th>Booking Ref</th>
                <th>Bin Size</th>
                <th>Delivery Date</th>
                <th>Status</th>
                <th>Total Paid</th>
                <th>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 ? (
                <tr>
                  <td colSpan="6" className="ma-empty">
                    <p>No bookings yet.</p>
                    <button className="ma-btn-small" onClick={() => navigate("/booking")}>
                      Book Your First Bin
                    </button>
                  </td>
                </tr>
              ) : (
                bookings.slice(0, 4).map((b) => (
                  <tr key={b.booking_id} className="ma-table-row">
                    <td className="ma-booking-id">
                      <strong>{b.booking_ref || `#${b.booking_id}`}</strong>
                    </td>
                    <td>
                      <span className="ma-bin-tag">{b.bin_size || b.size || "Standard"}</span>
                    </td>
                    <td>{fmt(b.delivery_date)}</td>
                    <td>
                      <span className={`ma-status ${STATUS_CLASS[b.status?.toLowerCase()] || ""}`}>
                        {b.status}
                      </span>
                    </td>
                    <td className="ma-amount">${Number(b.total_amount || 0).toFixed(2)}</td>
                    <td>
                      <button className="ma-table-inv-btn" onClick={() => onOpenInvoice(b)} title="View Tax Invoice">
                        <FaFileInvoiceDollar /> Invoice
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* MOBILE CARD VIEW */}
        <div className="ma-mobile-cards">
          {bookings.length === 0 ? (
            <div className="ma-empty">
              <p>No bookings yet.</p>
              <button className="ma-btn-small" onClick={() => navigate("/booking")}>
                Book Your First Bin
              </button>
            </div>
          ) : (
            bookings.slice(0, 4).map((b) => (
              <div key={b.booking_id} className="ma-mobile-item-card">
                <div className="ma-mobile-item-header">
                  <div className="ma-mobile-item-ref">
                    <strong>{b.booking_ref || `#${b.booking_id}`}</strong>
                    <span className="ma-bin-tag">{b.bin_size || b.size || "Standard"}</span>
                  </div>
                  <span className={`ma-status ${STATUS_CLASS[b.status?.toLowerCase()] || ""}`}>
                    {b.status}
                  </span>
                </div>
                <div className="ma-mobile-item-body">
                  <div className="ma-mobile-item-field">
                    <span>Delivery</span>
                    <strong>{fmt(b.delivery_date)}</strong>
                  </div>
                  <div className="ma-mobile-item-field">
                    <span>Total Paid</span>
                    <strong className="ma-amount">${Number(b.total_amount || 0).toFixed(2)}</strong>
                  </div>
                </div>
                <div className="ma-mobile-item-footer">
                  <button className="ma-table-inv-btn" onClick={() => onOpenInvoice(b)}>
                    <FaDownload /> Download Tax Invoice
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════
   SECTION 2: My Bookings (Full List + Filters)
════════════════════════════════════════════ */
const MyBookingsView = ({ bookings, onOpenInvoice, navigate }) => {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const statuses = ["all", "new", "confirmed", "active", "completed", "cancelled"];

  const filtered = bookings.filter((b) => {
    const matchesFilter = filter === "all" || b.status?.toLowerCase() === filter;
    const matchesSearch =
      !search ||
      (b.booking_ref && b.booking_ref.toLowerCase().includes(search.toLowerCase())) ||
      (b.bin_size && b.bin_size.toLowerCase().includes(search.toLowerCase())) ||
      (b.delivery_address && b.delivery_address.toLowerCase().includes(search.toLowerCase())) ||
      String(b.booking_id).includes(search);
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="ma-main-body">
      <div className="ma-main-header">
        <div>
          <h1 className="ma-title">My Bookings</h1>
          <p className="ma-subtitle">Track, review, and download invoices for all your skip bin hires.</p>
        </div>
        <button className="ma-btn-primary" onClick={() => navigate("/booking")}>
          <FaPlus /> Book Another Bin
        </button>
      </div>

      <div className="ma-card">
        {/* FILTERS & SEARCH BAR */}
        <div className="ma-controls-bar">
          <div className="ma-filter-row">
            <FaFilter style={{ color: "#8A97B0", fontSize: "0.85rem" }} />
            {statuses.map((s) => (
              <button
                key={s}
                className={`ma-filter-btn ${filter === s ? "ma-filter-active" : ""}`}
                onClick={() => setFilter(s)}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          <div className="ma-search-box">
            <FaSearch className="ma-search-icon" />
            <input
              type="text"
              placeholder="Search by Ref # or Address..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && <button className="ma-search-clear" onClick={() => setSearch("")}>×</button>}
          </div>
        </div>

        {/* DESKTOP / TABLET TABLE */}
        <div className="ma-table-wrap ma-desktop-table">
          <table className="ma-table">
            <thead>
              <tr>
                <th>Booking Ref</th>
                <th>Bin & Waste</th>
                <th>Delivery Schedule</th>
                <th>Delivery Address</th>
                <th>Status</th>
                <th>Total Paid</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="7" className="ma-empty">
                    <p>No bookings matching your filter.</p>
                  </td>
                </tr>
              ) : (
                filtered.map((b) => (
                  <tr key={b.booking_id} className="ma-table-row">
                    <td className="ma-booking-id">
                      <strong>{b.booking_ref || `#${b.booking_id}`}</strong>
                      <small className="ma-date-sub">{fmt(b.created_at || b.delivery_date)}</small>
                    </td>
                    <td>
                      <strong>{b.bin_size || b.size || "Standard"}</strong>
                      <small className="ma-date-sub">{b.waste_name || "General Waste"}</small>
                    </td>
                    <td>
                      <div><strong>Del:</strong> {fmt(b.delivery_date)}</div>
                      <small className="ma-date-sub"><strong>Col:</strong> {fmt(b.collection_date)}</small>
                    </td>
                    <td className="ma-address-cell">{b.delivery_address || "—"}</td>
                    <td>
                      <span className={`ma-status ${STATUS_CLASS[b.status?.toLowerCase()] || ""}`}>
                        {b.status}
                      </span>
                    </td>
                    <td className="ma-amount">${Number(b.total_amount || 0).toFixed(2)}</td>
                    <td>
                      <button className="ma-table-inv-btn" onClick={() => onOpenInvoice(b)}>
                        <FaFileInvoiceDollar /> Tax Invoice
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* MOBILE CARDS FOR BOOKINGS */}
        <div className="ma-mobile-cards">
          {filtered.length === 0 ? (
            <div className="ma-empty">
              <p>No bookings matching your filter.</p>
            </div>
          ) : (
            filtered.map((b) => (
              <div key={b.booking_id} className="ma-mobile-item-card">
                <div className="ma-mobile-item-header">
                  <div className="ma-mobile-item-ref">
                    <strong>{b.booking_ref || `#${b.booking_id}`}</strong>
                    <small className="ma-date-sub">{fmt(b.created_at || b.delivery_date)}</small>
                  </div>
                  <span className={`ma-status ${STATUS_CLASS[b.status?.toLowerCase()] || ""}`}>
                    {b.status}
                  </span>
                </div>
                <div className="ma-mobile-item-body">
                  <div className="ma-mobile-item-field">
                    <span>Bin & Waste</span>
                    <strong>{b.bin_size || b.size || "Standard"} • {b.waste_name || "General"}</strong>
                  </div>
                  <div className="ma-mobile-item-field">
                    <span>Schedule</span>
                    <strong>Del: {fmt(b.delivery_date)} | Col: {fmt(b.collection_date)}</strong>
                  </div>
                  {b.delivery_address && (
                    <div className="ma-mobile-item-field">
                      <span>Address</span>
                      <strong className="ma-address-truncate">{b.delivery_address}</strong>
                    </div>
                  )}
                  <div className="ma-mobile-item-field">
                    <span>Total Paid</span>
                    <strong className="ma-amount">${Number(b.total_amount || 0).toFixed(2)}</strong>
                  </div>
                </div>
                <div className="ma-mobile-item-footer">
                  <button className="ma-table-inv-btn" onClick={() => onOpenInvoice(b)}>
                    <FaDownload /> Download Tax Invoice
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════
   SECTION 3: Tax Invoices Tab
════════════════════════════════════════════ */
const InvoicesView = ({ invoices, bookings, onOpenInvoice }) => {
  const invoiceList = invoices.length > 0 ? invoices : bookings.map((b) => ({
    invoice_number: `INV-${(b.created_at || "2026").toString().slice(0, 4)}-${(b.booking_id || 1).toString().padStart(5, "0")}`,
    booking_id: b.booking_id,
    booking_ref: b.booking_ref || `JB-${(b.booking_id || 1).toString().padStart(6, "0")}`,
    invoice_date: (b.created_at || "").slice(0, 10) || (b.delivery_date || "").slice(0, 10),
    delivery_date: b.delivery_date,
    total_amount: Number(b.total_amount || 0),
    status: "PAID",
    bin_size: b.bin_size || b.size || "Standard",
    waste_name: b.waste_name || "General Waste",
  }));

  return (
    <div className="ma-main-body">
      <div className="ma-main-header">
        <div>
          <h1 className="ma-title">Tax Invoices & Receipts</h1>
          <p className="ma-subtitle">
            All your official Australian Tax Invoices with GST breakdown, stored securely for accounting and records.
          </p>
        </div>
      </div>

      <div className="ma-card">
        {/* DESKTOP / TABLET TABLE */}
        <div className="ma-table-wrap ma-desktop-table">
          <table className="ma-table">
            <thead>
              <tr>
                <th>Invoice Number</th>
                <th>Booking Ref</th>
                <th>Invoice Date</th>
                <th>Service Description</th>
                <th>Status</th>
                <th>Amount (incl. GST)</th>
                <th>Download / Print</th>
              </tr>
            </thead>
            <tbody>
              {invoiceList.length === 0 ? (
                <tr>
                  <td colSpan="7" className="ma-empty">
                    <p>No invoices found. Once you book a skip bin, your tax invoice will appear here automatically.</p>
                  </td>
                </tr>
              ) : (
                invoiceList.map((inv) => (
                  <tr key={inv.booking_id} className="ma-table-row">
                    <td className="ma-invoice-code">
                      <FaFileInvoiceDollar style={{ color: "#F5821F", marginRight: 6 }} />
                      <strong>{inv.invoice_number}</strong>
                    </td>
                    <td>
                      <span className="ma-ref-pill">{inv.booking_ref}</span>
                    </td>
                    <td>{fmt(inv.invoice_date || inv.delivery_date)}</td>
                    <td>
                      <span>{inv.bin_size} Skip Bin ({inv.waste_name})</span>
                    </td>
                    <td>
                      <span className="inv-badge-paid-small">
                        <FaCheckCircle /> PAID
                      </span>
                    </td>
                    <td className="ma-amount">${Number(inv.total_amount || 0).toFixed(2)}</td>
                    <td>
                      <button className="ma-table-inv-btn" onClick={() => onOpenInvoice(inv)}>
                        <FaFileInvoiceDollar /> View / Print PDF
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* MOBILE CARDS FOR INVOICES */}
        <div className="ma-mobile-cards">
          {invoiceList.length === 0 ? (
            <div className="ma-empty">
              <p>No invoices found. Once you book a skip bin, your tax invoice will appear here automatically.</p>
            </div>
          ) : (
            invoiceList.map((inv) => (
              <div key={inv.booking_id} className="ma-mobile-item-card">
                <div className="ma-mobile-item-header">
                  <div className="ma-mobile-item-ref">
                    <span className="ma-invoice-code">
                      <FaFileInvoiceDollar style={{ color: "#F5821F", marginRight: 6 }} />
                      <strong>{inv.invoice_number}</strong>
                    </span>
                    <span className="ma-ref-pill">{inv.booking_ref}</span>
                  </div>
                  <span className="inv-badge-paid-small">
                    <FaCheckCircle /> PAID
                  </span>
                </div>
                <div className="ma-mobile-item-body">
                  <div className="ma-mobile-item-field">
                    <span>Service</span>
                    <strong>{inv.bin_size} Skip Bin ({inv.waste_name})</strong>
                  </div>
                  <div className="ma-mobile-item-field">
                    <span>Invoice Date</span>
                    <strong>{fmt(inv.invoice_date || inv.delivery_date)}</strong>
                  </div>
                  <div className="ma-mobile-item-field">
                    <span>Amount (incl. GST)</span>
                    <strong className="ma-amount">${Number(inv.total_amount || 0).toFixed(2)}</strong>
                  </div>
                </div>
                <div className="ma-mobile-item-footer">
                  <button className="ma-table-inv-btn" onClick={() => onOpenInvoice(inv)}>
                    <FaDownload /> Download Tax Invoice
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════
   SECTION 4: Profile Information
════════════════════════════════════════════ */
const ProfileView = ({ customer, setCustomer }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    first_name: customer?.first_name || "",
    last_name: customer?.last_name || "",
    mobile: customer?.mobile || "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: "", ok: true });

  useEffect(() => {
    setForm({
      first_name: customer?.first_name || "",
      last_name: customer?.last_name || "",
      mobile: customer?.mobile || "",
    });
  }, [customer]);

  const saveProfile = async () => {
    setSaving(true);
    setMsg({ text: "", ok: true });
    try {
      const res = await updateProfile(form);
      if (res.success && res.customer) {
        setCustomer(res.customer);
        setMsg({ text: "Profile updated successfully.", ok: true });
        setEditing(false);
      } else {
        setMsg({
          text: res.error || "Failed to save profile.",
          ok: false,
        });
      }
    } catch (e) {
      setMsg({
        text: e.message || "Failed to save profile.",
        ok: false,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ma-main-body">
      <div className="ma-main-header">
        <div>
          <h1 className="ma-title">Profile Information</h1>
          <p className="ma-subtitle">Manage your personal and contact details.</p>
        </div>
      </div>

      {/* ACCOUNT DETAILS CARD */}
      <div className="ma-card ma-profile-view-card">
        <div className="ma-card-header">
          <div className="ma-card-title-group">
            <FaUserCircle style={{ color: "#F5821F" }} />
            <h2>Account Details</h2>
          </div>
          {!editing ? (
            <button className="ma-edit-btn" onClick={() => { setMsg({ text: "", ok: true }); setEditing(true); }}>
              ✏️ Edit Profile
            </button>
          ) : (
            <div className="ma-card-actions">
              <button className="ma-save-btn" onClick={saveProfile} disabled={saving}>
                {saving ? "Saving…" : "Save Changes"}
              </button>
              <button className="ma-cancel-btn" onClick={() => { setEditing(false); setMsg({ text: "", ok: true }); }}>
                Cancel
              </button>
            </div>
          )}
        </div>

        {msg.text && (
          <div className={`ma-inline-msg ${msg.ok ? "ma-inline-ok" : "ma-inline-err"}`}>
            {msg.text}
          </div>
        )}

        <div className="ma-info-list">
          {editing ? (
            <>
              <div className="ma-edit-row">
                <label>First Name</label>
                <input
                  type="text"
                  placeholder="Enter first name"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                />
              </div>
              <div className="ma-edit-row">
                <label>Last Name</label>
                <input
                  type="text"
                  placeholder="Enter last name"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                />
              </div>
              <div className="ma-edit-row">
                <label>Mobile Number</label>
                <input
                  type="tel"
                  placeholder="e.g. 0495 000 017"
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                />
              </div>
              <div className="ma-info-row ma-info-row-static" style={{ borderBottom: "none" }}>
                <span className="ma-info-label">Email Address (read-only)</span>
                <span className="ma-info-val ma-info-val-muted">
                  <FaLock style={{ fontSize: 11, marginRight: 6, color: "#94A3B8" }} />
                  {customer?.email}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="ma-info-row">
                <span className="ma-info-label">First Name</span>
                <span className="ma-info-val">{customer?.first_name || "—"}</span>
              </div>
              <div className="ma-info-row">
                <span className="ma-info-label">Last Name</span>
                <span className="ma-info-val">{customer?.last_name || "—"}</span>
              </div>
              <div className="ma-info-row">
                <span className="ma-info-label">Email Address</span>
                <span className="ma-info-val">{customer?.email}</span>
              </div>
              <div className="ma-info-row">
                <span className="ma-info-label">Mobile Number</span>
                <span className="ma-info-val">{customer?.mobile || "—"}</span>
              </div>
              <div className="ma-info-row">
                <span className="ma-info-label">Account Type</span>
                <span className="ma-info-val">Residential Customer</span>
              </div>
              <div className="ma-info-row">
                <span className="ma-info-label">Member Since</span>
                <span className="ma-info-val">{fmt(customer?.created_at) || "2026"}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════
   SECTION 5: Change Password
════════════════════════════════════════════ */
const ChangePasswordView = () => {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [show, setShow] = useState({ current: false, newp: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: "", ok: true });

  const checks = {
    length: form.new_password.length >= 8,
    upper: /[A-Z]/.test(form.new_password),
    lower: /[a-z]/.test(form.new_password),
    number: /[0-9]/.test(form.new_password),
    special: /[^A-Za-z0-9]/.test(form.new_password),
    match: form.new_password === form.confirm_password && form.new_password.length > 0,
  };
  const allGood = Object.values(checks).every(Boolean) && form.current_password.length > 0;

  const submit = async () => {
    setSaving(true);
    setMsg({ text: "", ok: true });
    try {
      await api.put("/api/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setMsg({ text: "Password changed successfully.", ok: true });
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) {
      setMsg({ text: e.response?.data?.message || e.response?.data?.error || "Failed to change password.", ok: false });
    } finally {
      setSaving(false);
    }
  };

  const Eye = ({ field }) => (
    <button
      type="button"
      className="ma-eye-btn"
      onClick={() => setShow((s) => ({ ...s, [field]: !s[field] }))}
      aria-label="Toggle password visibility"
    >
      {show[field] ? <FaEyeSlash /> : <FaEye />}
    </button>
  );

  return (
    <div className="ma-main-body">
      <div className="ma-main-header">
        <div>
          <h1 className="ma-title">Change Password</h1>
          <p className="ma-subtitle">Keep your account secure with a strong password.</p>
        </div>
      </div>

      <div className="ma-card ma-pw-card">
        {msg.text && (
          <div className={`ma-inline-msg ${msg.ok ? "ma-inline-ok" : "ma-inline-err"}`}>{msg.text}</div>
        )}

        <div className="ma-pw-form">
          {[
            { key: "current", field: "current_password", label: "Current Password", placeholder: "Enter current password" },
            { key: "newp", field: "new_password", label: "New Password", placeholder: "Enter new password" },
            { key: "confirm", field: "confirm_password", label: "Confirm New Password", placeholder: "Re-enter new password" },
          ].map(({ key, field, label, placeholder }) => (
            <div className="ma-field" key={key}>
              <label>{label}</label>
              <div className="ma-input-wrap">
                <input
                  type={show[key] ? "text" : "password"}
                  placeholder={placeholder}
                  value={form[field]}
                  onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                />
                <Eye field={key} />
              </div>
            </div>
          ))}

          {form.new_password.length > 0 && (
            <div className="ma-pw-checks">
              {[
                { key: "length", label: "At least 8 characters" },
                { key: "upper", label: "One uppercase letter (A-Z)" },
                { key: "lower", label: "One lowercase letter (a-z)" },
                { key: "number", label: "One number (0-9)" },
                { key: "special", label: "One special character (!@#$%^&*)" },
                { key: "match", label: "Passwords match" },
              ].map((c) => (
                <div key={c.key} className={`ma-pw-check ${checks[c.key] ? "ma-check-ok" : "ma-check-no"}`}>
                  {checks[c.key] ? <FaCheck /> : <FaTimes />}
                  <span>{c.label}</span>
                </div>
              ))}
            </div>
          )}

          <button className="ma-save-btn" onClick={submit} disabled={!allGood || saving}>
            {saving ? "Updating…" : "Update Password"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════
   ROOT MY ACCOUNT / CUSTOMER PORTAL COMPONENT
════════════════════════════════════════════ */
const MyAccountPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "overview";

  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loadErr, setLoadErr] = useState(false);

  // Sync tab with URL search parameter changes (e.g. clicking dropdown or back/forward)
  useEffect(() => {
    const tab = searchParams.get("tab") || "overview";
    setActiveTab(tab);
  }, [searchParams]);

  // Invoice modal state
  const [activeInvoiceData, setActiveInvoiceData] = useState(null);
  const [activeBookingForInvoice, setActiveBookingForInvoice] = useState(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const profRes = await getProfile();
      if (profRes.success) {
        setCustomer(profRes.customer);
      } else {
        setLoadErr(true);
      }
    } catch {
      setLoadErr(true);
    }

    try {
      const bookRes = await getBookings();
      setBookings(bookRes.bookings || []);
    } catch {
      setBookings([]);
    }

    try {
      const invList = await getMyInvoices();
      setInvoices(invList || []);
    } catch {
      setInvoices([]);
    }
  };

  const handleOpenInvoice = async (item) => {
    const bookingId = item.booking_id;
    setActiveBookingForInvoice(item);
    try {
      const fullInvoice = await getInvoice(bookingId);
      if (fullInvoice) {
        setActiveInvoiceData(fullInvoice);
      } else {
        setActiveInvoiceData(null);
      }
    } catch {
      setActiveInvoiceData(null);
    }
    setInvoiceModalOpen(true);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleNav = (key) => {
    setMobileOpen(false);
    if (key === "logout") {
      handleLogout();
      return;
    }
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  const activeItem = NAV_ITEMS.find((i) => i.key === activeTab);

  if (loadErr) {
    return (
      <>
        <WebsiteNavbar />
        <div className="ma-loading-wrap">
          <div className="ma-err-box">
            <div className="ma-err-icon">
              <FaLock />
            </div>
            <h2>Session Expired</h2>
            <p>Please log in again to continue.</p>
            <button className="ma-btn-primary" onClick={() => navigate("/loginpage")}>
              Go to Login
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!customer) {
    return (
      <>
        <WebsiteNavbar />
        <div className="ma-loading-wrap">
          <div className="ma-spinner" />
          <p>Loading your dashboard…</p>
        </div>
        <Footer />
      </>
    );
  }

  const renderMain = () => {
    switch (activeTab) {
      case "overview":
        return (
          <DashboardOverview
            customer={customer}
            bookings={bookings}
            invoices={invoices}
            navigate={navigate}
            setTab={handleNav}
            onOpenInvoice={handleOpenInvoice}
          />
        );
      case "bookings":
        return (
          <MyBookingsView
            bookings={bookings}
            onOpenInvoice={handleOpenInvoice}
            navigate={navigate}
          />
        );
      case "invoices":
        return (
          <InvoicesView
            invoices={invoices}
            bookings={bookings}
            onOpenInvoice={handleOpenInvoice}
          />
        );
      case "profile":
        return <ProfileView customer={customer} setCustomer={setCustomer} />;
      case "password":
        return <ChangePasswordView />;
      default:
        return null;
    }
  };

  return (
    <>
      <WebsiteNavbar />

      <div className="ma-page">
        <div className="ma-container">
          {/* SIDEBAR NAVIGATION */}
          <aside className="ma-sidebar" ref={dropdownRef}>
            {/* PROFILE BADGE CARD */}
            <div className="ma-profile-card">
              <div className="ma-avatar">
                <FaUserCircle />
              </div>
              <div className="ma-profile-info">
                <h3>{customer.first_name} {customer.last_name || ""}</h3>
                <p>{customer.email}</p>
              </div>
            </div>

            {/* DESKTOP NAV */}
            <nav className="ma-nav ma-nav-desktop">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.key}
                  className={`ma-nav-item ${activeTab === item.key ? "ma-nav-active" : ""} ${item.key === "logout" ? "ma-nav-logout" : ""}`}
                  onClick={() => handleNav(item.key)}
                >
                  <span className="ma-nav-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              ))}
            </nav>

            {/* MOBILE DROPDOWN TRIGGER */}
            <div
              className="ma-mobile-bar"
              onClick={() => setMobileOpen((o) => !o)}
              role="button"
              aria-expanded={mobileOpen}
            >
              <div className="ma-mobile-bar-left">
                <div className="ma-mobile-bar-avatar"><FaUserCircle /></div>
                <div className="ma-mobile-bar-info">
                  <h3>{customer.first_name} {customer.last_name || ""}</h3>
                  <div className="ma-mobile-active-label"><span>{activeItem?.label}</span></div>
                </div>
              </div>
              <FaChevronDown className={`ma-mobile-chevron ${mobileOpen ? "open" : ""}`} />
            </div>

            {/* MOBILE DROPDOWN */}
            <div className={`ma-mobile-dropdown ${mobileOpen ? "open" : ""}`}>
              <nav className="ma-nav">
                {NAV_ITEMS.map((item) => (
                  <button
                    key={item.key}
                    className={`ma-nav-item ${activeTab === item.key ? "ma-nav-active" : ""} ${item.key === "logout" ? "ma-nav-logout" : ""}`}
                    onClick={() => handleNav(item.key)}
                  >
                    <span className="ma-nav-icon">{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* QUICK SUPPORT CARD */}
            <div className="ma-help-card">
              <div className="ma-help-icon"><FaHeadset /></div>
              <div>
                <h4>Need Bin Support?</h4>
                <p>Call our delivery team or send a message.</p>
                <button className="ma-help-btn" onClick={() => navigate("/contact")}>
                  Contact Support →
                </button>
              </div>
            </div>
          </aside>

          {/* MAIN CONTENT AREA */}
          <main className="ma-main">
            {renderMain()}
          </main>
        </div>
      </div>

      {/* TAX INVOICE MODAL */}
      {invoiceModalOpen && (
        <InvoiceModal
          invoice={activeInvoiceData}
          booking={activeBookingForInvoice}
          onClose={() => setInvoiceModalOpen(false)}
        />
      )}

      <Footer />
    </>
  );
};

export default MyAccountPage;